import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

interface MLAbuseDetectionData {
  result_id: string;
  title: string;
  timestamp: string;
  domain: string;
  userId: string;
  projectId: string;
  environment: string;
  ML_Abuse_Detection: Array<{
    Category: string;
    'Severity Score': number;
    Risk: string;
    Action: string;
    Observation: string;
  }>;
  Overall_Abuse_Score: number;
}

interface MLAbuseDetectionChartProps {
  data: MLAbuseDetectionData[];
  title: string;
  description: string;
}

const MLAbuseDetectionChart: React.FC<MLAbuseDetectionChartProps> = ({ data, title, description }) => {
  const { getThemeColor } = useAppContext();

  // Process data to extract categories and their severity scores
  const chartData = React.useMemo((): Array<{
    category: string;
    meanSeverity: number;
  }> => {
    if (!data || data.length === 0) {
      return [];
    }

    // Collect all categories and their scores
    const categoryMap = new Map<string, number[]>();

    data.forEach(result => {
      if (result.ML_Abuse_Detection && Array.isArray(result.ML_Abuse_Detection)) {
        result.ML_Abuse_Detection.forEach(item => {
          const category = item.Category;
          const severity = item['Severity Score'] * 100; // Convert to percentage

          if (!categoryMap.has(category)) {
            categoryMap.set(category, []);
          }
          categoryMap.get(category)!.push(severity);
        });
      }
    });

    // Calculate mean severity for each category
    const result: { category: string; meanSeverity: number }[] = [];
    categoryMap.forEach((scores, category) => {
      const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      result.push({ category, meanSeverity: mean });
    });

    // Sort by mean severity descending
    return result.sort((a, b) => b.meanSeverity - a.meanSeverity);
  }, [data]);

  // Ensure we have valid colors with fallbacks
  const dangerColor = getThemeColor('danger') || '#dc3545';
  const warningColor = getThemeColor('warning') || '#ffc107';
  const infoColor = getThemeColor('info') || '#0dcaf0';
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const categories = chartData.map((item: { category: string; meanSeverity: number }) => item.category);
  const meanSeverities = chartData.map((item: { category: string; meanSeverity: number }) => item.meanSeverity);

  // Color mapping based on risk level
  const getColorForCategory = (category: string) => {
    // Find the risk level for this category from the data
    for (const result of data) {
      if (result.ML_Abuse_Detection) {
        const item = result.ML_Abuse_Detection.find(d => d.Category === category);
        if (item) {
          const risk = item.Risk?.toLowerCase() || '';
          if (risk === 'high') return dangerColor;
          if (risk === 'medium') return warningColor;
          if (risk === 'low') return infoColor;
        }
      }
    }
    return primaryColor;
  };

  const options = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        const category = param.name;
        const severity = param.value;

        // Find additional info for this category
        let risk = '';
        let observation = '';
        for (const result of data) {
          if (result.ML_Abuse_Detection) {
            const item = result.ML_Abuse_Detection.find(d => d.Category === category);
            if (item) {
              risk = item.Risk || '';
              observation = item.Observation || '';
              break;
            }
          }
        }

        return `
          <strong>${category}</strong><br/>
          Mean Severity: ${severity.toFixed(1)}%<br/>
          Risk: ${risk}<br/>
          ${observation ? `<small>${observation}</small>` : ''}
        `;
      }
    },
    xAxis: {
      type: 'category',
      name: 'Category',
      data: categories,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: {
        color: secondaryColor,
        rotate: 0,
        interval: 0
      },
      nameTextStyle: { color: secondaryColor }
    },
    yAxis: {
      type: 'value',
      name: 'Mean Severity (%)',
      min: 0,
      max: Math.max(...meanSeverities, 35),
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor }
    },
    series: [
      {
        type: 'bar',
        data: meanSeverities.map((severity: number, index: number) => ({
          value: severity,
          itemStyle: {
            borderRadius: [5, 5, 0, 0],
            color: getColorForCategory(categories[index])
          }
        })),
        barWidth: '50%',
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => `${params.value.toFixed(1)}%`,
          color: lightTextEmphasis
        }
      }
    ],
    grid: { left: '15%', right: '10%', bottom: '15%', top: '15%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">{title}</h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '400px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default MLAbuseDetectionChart;
